/**
 * F17 T17.1.6 v3 Commit 6 — wspólna tabela dla zakładek "Moje zgłoszenia" +
 * "Wszystkie aktywne".
 *
 * Step-level zakładka "Moje czekające" ma własną tabelę w
 * `MyPendingDecisionsTab.tsx` (inny shape: stepInstanceId vs id, jeden
 * currentNodeLabel vs array currentNodeLabels).
 *
 * Props `showSubmitter` — w "Moje zgłoszenia" nie ma sensu pokazywać kolumny
 * "Zlecający" (zawsze sam user). W "Wszystkie aktywne" jest istotne kto zlecił.
 *
 * Konwersja z raw `<table>` na `ConfigurableTable` (filtry per kolumna,
 * server-side) — mirror `KsefInvoices.tsx`/`ProcessesListPage.tsx`. Kolumny
 * budowane wewnątrz tego wrappera (`buildInstanceListingColumns`) z
 * `showSubmitter` — obaj konsumenci (`ActiveInstancesTab`, `MySubmittedInstancesTab`)
 * dostają IDENTYCZNY zestaw kolumn poza "Zlecający", zero duplikacji.
 *
 * `headerFilters`/`onHeaderFiltersChange` są KONTROLOWANE przez wołającego
 * (Tab) — oba endpointy (`/instances/all`, `/my-submitted-instances`) są
 * server-paginowane (limit/offset), więc filtr per kolumna MUSI iść na
 * backend przez `?headerFilters=`, nie lokalnie na już pobranej stronie
 * (ten sam próg co `KsefInvoices.tsx`/`ProcessesListPage.tsx`).
 *
 * **Które kolumny mają `headerFilter`/`sortable`** — surowe kolumny `process_instances`
 * (`instanceNumber`/`status`/`submittedAt`/`targetModuleCode`) ORAZ `processName`
 * (oba ekrany) i `submittedBy` (tylko gdy `showSubmitter` — kolumna „Zlecający" jest
 * pokazywana WYŁĄCZNIE na „Wszystkie aktywne"), dociągnięte LEFT JOIN-em wprost w
 * zapytaniach backendu (`HEADER_FILTER_FIELDS`/`sortFields` w obu route.ts). **Fala 2
 * kokpitu decyzji** dołożyła filtr+sort po `target_label` (wyrażenie SQL — aktualna nazwa
 * rekordu, `targetLabel` z `buildRecordExpressions` w `decisions-list/expressions.ts` —
 * sortowalne, bo skalar) oraz WYŁĄCZNIE filtr po `current_node` (na ziarnie instancji krok
 * bywa wielowartościowy —
 * `buildCurrentStepInstanceFilter`, `EXISTS` po wszystkich aktualnych węzłach — więc bez
 * jednoznacznego sortu; `sortable: false` tu jest ŚWIADOME, nie przeoczenie, w
 * przeciwieństwie do „Moje czekające" gdzie krok jest pojedynczy i sortowalny).
 * `conditionOnly: true` WYŁĄCZNIE na kolumnach tekstowych
 * (`target_module_code`/`process_name`/`submitted_by`/`status`/`target_label`/`current_node`)
 * — zakładka „Wartości" popupu filtra wymaga endpointu `/distinct`, którego procesy nie
 * mają. Kolumny liczba/data (`instance_number`/`submitted_at`) NIE dostają tej flagi:
 * `conditionOnly` gasi RÓWNIEŻ zakładkę „Zakres" (`ColumnHeaderFilterPopup.tsx`), a zakres dla
 * liczby/daty działa end-to-end — gaszenie go byłoby utratą działającej funkcji.
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DecisionGroupBy } from '@saas/shared';
import type { TFunction } from 'i18next';

import {
  type ColumnDefinition,
  type ColumnHeaderFilter,
  type ColumnHeaderFilters,
  ConfigurableTable,
  type ConfigurableTableProps,
} from '@/components/configurable-table';
import {
  displaySubmitter,
  type InstanceListingRow,
  type InstanceStatus,
} from '@/lib/processes/approvals-api';
import {
  CUSTOM_MODULE_CODE_PREFIX,
  isTargetNavigable,
  moduleLabelI18nKey,
} from '@/lib/processes/module-routes';
import { FINAL_NODE_CLASS, finalNodeLabelOrNull } from '@/lib/processes/step-tone';
import { useNavigateToTarget } from '@/lib/processes/use-navigate-to-target';
import { documentClassLabelKey } from '@/lib/system-entity-adapters';

const STATUS_VARIANT_CLASS: Record<InstanceStatus, string> = {
  active: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-700',
  suspended: 'bg-yellow-100 text-yellow-800',
  error: 'bg-orange-100 text-orange-800',
};

/**
 * Serializuje `ColumnHeaderFilters` (kolumnId → filtr, stan UI) do `?headerFilters=`
 * (JSON.stringify HeaderFilter[]) — mirror `KsefInvoices.tsx`/`ProcessesListPage.tsx`.
 * KLUCZOWE: `field` w wyjściowym obiekcie MUSI być `headerFilter.fieldSlug` kolumny,
 * NIE `columnId` z UI — kontrakt z whitelistą `HEADER_FILTER_FIELDS` w
 * `apps/api/app/api/processes/instances/all/route.ts` (+ `my-submitted-instances/route.ts`)
 * operuje na backendowych slugach. Czysta funkcja (generyczna po `T`, mirror
 * KsefInvoices/ProcessesListPage) — używana przez OBIE zakładki (`ActiveInstancesTab`,
 * `MySubmittedInstancesTab`), stąd eksportowana z tego wspólnego wrappera zamiast
 * duplikowana w każdej.
 */
export function serializeHeaderFiltersForRequest<T>(
  headerFilters: ColumnHeaderFilters,
  columns: ColumnDefinition<T>[],
): string | undefined {
  const merged: ColumnHeaderFilter[] = [];
  for (const [columnId, filter] of Object.entries(headerFilters)) {
    const col = columns.find((c) => c.id === columnId);
    const fieldSlug = col?.headerFilter?.fieldSlug ?? columnId;
    if (fieldSlug) merged.push({ ...filter, field: fieldSlug });
  }
  return merged.length > 0 ? JSON.stringify(merged) : undefined;
}

/**
 * Buduje kolumny listy instancji. Wywoływana WEWNĄTRZ `InstanceListingTable`
 * (render) i przez każdą zakładkę (do serializacji `headerFilters` przed
 * wysłaniem zapytania) — czysta funkcja, jedno źródło prawdy dla mapowania
 * kolumna ↔ `fieldSlug`.
 */
export function buildInstanceListingColumns(
  showSubmitter: boolean,
  t: TFunction,
): ColumnDefinition<InstanceListingRow>[] {
  const columns: ColumnDefinition<InstanceListingRow>[] = [
    {
      id: 'instance_number',
      accessor: 'instanceNumber',
      header: t('tenant.processes.approvals.columns.instanceNumber'),
      defaultWidth: 90,
      sortable: true,
      // `conditionOnly` pominięte dla number/date — patrz komentarz nagłówkowy funkcji.
      headerFilter: { category: 'number', fieldSlug: 'instance_number' },
      renderCell: (_value, row) => <span className="font-mono text-xs">#{row.instanceNumber}</span>,
    },
    {
      id: 'process_name',
      accessor: 'processName',
      header: t('tenant.processes.approvals.columns.processName'),
      defaultWidth: 220,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'process_name', conditionOnly: true },
      renderCell: (_value, row) =>
        row.processName ?? (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingProcessName')}
          </span>
        ),
    },
    {
      id: 'target_module_code',
      accessor: 'targetModuleCode',
      header: t('tenant.processes.approvals.columns.module'),
      defaultWidth: 160,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'target_module_code', conditionOnly: true },
      renderCell: (_value, row) => (
        <span className="text-text-tertiary">{t(moduleLabelI18nKey(row.targetModuleCode))}</span>
      ),
    },
    {
      id: 'target_label',
      accessor: 'targetLabel',
      header: t('tenant.processes.approvals.columns.target'),
      defaultWidth: 200,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'target_label', conditionOnly: true },
      renderCell: (_value, row) =>
        row.targetLabel ?? (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingTarget')}
          </span>
        ),
    },
    {
      id: 'current_node',
      accessor: (row) => row.currentNodeLabels.filter((l): l is string => l !== null).join(' / '),
      header: t('tenant.processes.approvals.columns.currentNode'),
      defaultWidth: 200,
      // Krok jest wielowartościowy na ziarnie instancji (parallel) — filtrowalny (EXISTS po
      // wszystkich aktualnych węzłach), ale NIE sortowalny (bez jednoznacznego skalara do
      // ORDER BY). Kontrast z „Moje czekające" (`MyPendingDecisionsTab.tsx`), gdzie krok jest
      // pojedynczy i sortowalny — patrz komentarz nagłówkowy pliku.
      sortable: false,
      headerFilter: { category: 'text', fieldSlug: 'current_node', conditionOnly: true },
      renderCell: (value) => {
        const nodeLabel = String(value ?? '');
        return nodeLabel.length > 0 ? (
          nodeLabel
        ) : (
          <span className="text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingNode')}
          </span>
        );
      },
    },
  ];

  if (showSubmitter) {
    columns.push({
      id: 'submitted_by',
      accessor: (row) => (row.submittedByUser ? displaySubmitter(row.submittedByUser) : null),
      header: t('tenant.processes.approvals.columns.submittedBy'),
      defaultWidth: 180,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'submitted_by', conditionOnly: true },
      renderCell: (_value, row) =>
        row.submittedByUser ? (
          <span className="text-text-tertiary">{displaySubmitter(row.submittedByUser)}</span>
        ) : (
          <span className="italic text-text-tertiary">
            {t('tenant.processes.approvals.fallback.missingSubmitter')}
          </span>
        ),
    });
  }

  columns.push(
    {
      id: 'submitted_at',
      accessor: 'submittedAt',
      header: t('tenant.processes.approvals.columns.submittedAt'),
      defaultWidth: 140,
      sortable: true,
      // `conditionOnly` pominięte dla number/date — patrz komentarz nagłówkowy funkcji.
      headerFilter: { category: 'date', fieldSlug: 'submitted_at' },
      renderCell: (_value, row) => (
        <span className="text-xs text-text-tertiary">
          {new Date(row.submittedAt).toLocaleString('pl-PL')}
        </span>
      ),
    },
    {
      id: 'status',
      accessor: 'status',
      header: t('tenant.processes.approvals.columns.status'),
      defaultWidth: 120,
      sortable: true,
      headerFilter: { category: 'text', fieldSlug: 'status', conditionOnly: true },
      // Zakończona sprawa pokazuje nazwę etapu końcowego zamiast statusu technicznego (filtr,
      // sort i grupowanie po tej kolumnie dalej idą po statusie — `fieldSlug: 'status'`).
      renderCell: (_value, row) => {
        const finalLabel = finalNodeLabelOrNull(row.finalNodeLabel);
        return finalLabel ? (
          <span
            data-final-node="true"
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${FINAL_NODE_CLASS}`}
          >
            {finalLabel}
          </span>
        ) : (
          <span
            className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_VARIANT_CLASS[row.status]}`}
          >
            {t(`tenant.processes.approvals.status.${row.status}`)}
          </span>
        );
      },
    },
  );

  return columns;
}

export function InstanceListingTable({
  rows,
  showSubmitter,
  testIdPrefix,
  headerFilters,
  onHeaderFiltersChange,
  sortColumn,
  sortDirection,
  onSort,
  groups,
  expandedGroupKeys,
  onToggleGroup,
  toolbarExtra,
  emptyTitle,
}: {
  rows: InstanceListingRow[];
  showSubmitter: boolean;
  /** Prefix dla `data-testid` na wierszach (różny per tab). */
  testIdPrefix: string;
  /** Filtry per kolumna (Kreator Listy) — kontrolowane przez wołającego (server-side). */
  headerFilters: ColumnHeaderFilters;
  onHeaderFiltersChange: (filters: ColumnHeaderFilters) => void;
  /** Sort server-side (kontrolowany, mirror `headerFilters`) — `null`/`undefined` = brak sortu aktywnego. */
  sortColumn: string | undefined;
  sortDirection: 'asc' | 'desc';
  onSort: (column: string, direction: 'asc' | 'desc') => void;
  /**
   * Tryb grupowany (fala 2 kokpitu decyzji) — mirror `ConfigurableTable.groups` (mapowane
   * przez wołającego z odpowiedzi grupowanej, patrz `groupLabelText` niżej). Gdy podane,
   * `rows` jest ignorowane przez samą tabelę (renderuje koszyki zamiast płaskiej listy) —
   * wołający i tak przekazuje `[]` dla czytelności kontraktu (mirror `MyPendingDecisionsTab.tsx`).
   */
  groups?: ConfigurableTableProps<InstanceListingRow>['groups'];
  /** Mirror `ConfigurableTable.expandedGroupKeys` — sterowanie zwinięciem z zewnątrz (leniwe doładowanie). */
  expandedGroupKeys?: Set<string>;
  /** Mirror `ConfigurableTable.onToggleGroup`. */
  onToggleGroup?: (key: string) => void;
  /** Mirror `ConfigurableTable.toolbarExtra` (np. selektor wymiaru grupowania). */
  toolbarExtra?: ReactNode;
  /**
   * Mirror `ConfigurableTable.emptyTitle` — komunikat pustego stanu renderowany W ŚRODKU tabeli
   * (nagłówki kolumn zostają widoczne). Ekrany spraw podają własny (`empty.active`/`empty.submitted`),
   * żeby „brak danych" pokazywał strukturę listy zamiast samodzielnego komunikatu-sterty.
   */
  emptyTitle?: string;
}) {
  const { t } = useTranslation();
  const navigateToTarget = useNavigateToTarget();

  const columns = useMemo(() => buildInstanceListingColumns(showSubmitter, t), [showSubmitter, t]);

  const isGrouped = groups !== undefined;

  return (
    // Tabela — Kreator Listy (ConfigurableTable): filtry per kolumna server-side przez
    // `headerFilters` (kontrolowane, patrz props wyżej — endpointy są paginowane, filtr MUSI
    // iść na backend, nie po pobranej stronie). Wiersz filtrów renderuje kontrolki typowane
    // (menu operatorów + edytor) dla kolumn z `headerFilter`; kolumny bez niego NIE dostają
    // pola — ekran nie kontroluje `columnFilters`, więc `FilterRowCell` daje pusty placeholder
    // zamiast lokalnego filtra pobranej strony. `persistConfig={false}` — brak zapisu layoutu
    // (mirror KsefInvoices.tsx).
    <ConfigurableTable<InstanceListingRow>
      fillHeight
      tableId={`processes-instances-${testIdPrefix}`}
      testIdPrefix={testIdPrefix}
      columns={columns}
      data={isGrouped ? [] : rows}
      groups={groups}
      expandedGroupKeys={expandedGroupKeys}
      onToggleGroup={onToggleGroup}
      toolbarExtra={toolbarExtra}
      emptyTitle={emptyTitle}
      getRowId={(row) => row.id}
      headerFilters={headerFilters}
      onHeaderFiltersChange={onHeaderFiltersChange}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
      persistConfig={false}
      showViewModeToggle={false}
      onRowClick={(row) => navigateToTarget(row.targetModuleCode, row.targetRecordId)}
      // ConfigurableTable dokleja `cursor-pointer hover:bg-gray-50` do KAŻDEGO wiersza,
      // gdy `onRowClick` jest podany (niezależnie od per-wiersza klikalności) —
      // `!cursor-default` (important modifier, mirror np. `EmployeeNode.tsx`) wymusza
      // nadpisanie kursora dla wierszy bez `targetUrl` (nieznany/`custom.*` moduł), bo
      // `.cursor-pointer` i `.cursor-default` mają tę samą specyficzność i o wygranej
      // decydowałaby kolejność w skompilowanym CSS, nie kolejność klas w atrybucie.
      // `hover:!bg-transparent` gasi też podświetlenie pod myszką, żeby wiersz nieklikalny
      // nie udawał interaktywnego (parytet z pierwotną surową tabelą).
      rowClassName={(row) =>
        isTargetNavigable(row.targetModuleCode, row.targetRecordId)
          ? ''
          : '!cursor-default hover:!bg-transparent'
      }
    />
  );
}

// ============================================================================
// Etykieta koszyka grupowania — WSPÓLNA dla WSZYSTKICH 3 ekranów kokpitu decyzji
// (fala 2). Jedna implementacja, jeden switch wyczerpujący cały `DecisionGroupBy`
// (unia 8 wymiarów) — każdy ekran filtruje wcześniej po swojej białej liście
// (`PENDING_DECISION_GROUP_BY`/`SUBMITTED_INSTANCE_GROUP_BY`/`ACTIVE_INSTANCE_GROUP_BY`,
// `@saas/shared`), więc realnie widzi tylko podzbiór case'ów, ale funkcja MUSI
// obsłużyć cały union (parametr typowany `DecisionGroupBy`, nie podzbiorem).
// ============================================================================

/**
 * Etykieta koszyka grupowania — reguła per wymiar (kontrakt backendu, patrz
 * `apps/api/lib/processes/decisions-list/grouping.ts`):
 *
 *  - `process`/`submitter`/`company`/`current_node` niosą NAZWĘ z serwera w `label`
 *    (`null` — soft-deleted encja LUB koszyk „brak wartości" `'__none__'`, np. sprawa
 *    bez firmy / krok bez etykiety) → generyczny fallback „(brak)" przez i18n. Dla
 *    `current_node` `label` niesie DOSŁOWNĄ etykietę węzła ze snapshotu — front jej
 *    NIE tłumaczy (nie jest to enum, tylko tekst z buildera procesu).
 *  - `priority`/`deadline_status`/`status` niosą w `key` SUROWY enum — front tłumaczy
 *    (`status` reużywa DOKŁADNIE te same klucze i18n co kolumna „Status" listy instancji —
 *    jeden słownik, zero duplikatu).
 *  - `target_class` niesie w `key` `documents:<klasa>`, `handel:<rodzaj>` albo kod modułu —
 *    front rozstrzyga klasę dokumentu / kartotekę Handlu / etykietę modułu (mirror renderCell
 *    kolumny „Kartoteka"). Wyjątek: kartoteka własna (`custom.<slug>`) bierze nazwę z etykiety
 *    serwera (`label`), a komórka kolumny „Kartoteka" zostaje ogólna („Kartoteka niestandardowa") —
 *    świadoma różnica.
 */

/**
 * Rodzaj dokumentu Symfonii → klucz i18n kartoteki. Klucze wypisane WPROST — klucz sklejany
 * w locie jest niewidoczny dla bramki tłumaczeń, więc brak przekładu wyszedłby dopiero na ekranie.
 */
const HANDEL_KIND_LABEL_KEYS: Record<string, string | undefined> = {
  sale: 'tenant.handelMagazyn.sales.title',
  warehouse: 'tenant.handelMagazyn.warehouse.title',
  order: 'tenant.handelMagazyn.orders.title',
};
export function groupLabelText(
  t: TFunction,
  groupBy: DecisionGroupBy,
  group: { key: string; label: string | null },
): string {
  switch (groupBy) {
    case 'process':
    case 'submitter':
    case 'company':
    case 'current_node':
      return group.label ?? t('tenant.processes.approvals.groupBy.fallback.none');
    case 'priority':
      return t(`tenant.processes.priorityLabel.${group.key}`);
    case 'deadline_status':
      return t(`tenant.processes.approvals.groupBy.deadlineStatus.${group.key}`);
    case 'status':
      return t(`tenant.processes.approvals.status.${group.key}`);
    case 'target_class':
      if (group.key.startsWith('documents:')) {
        const docClass = group.key.slice('documents:'.length);
        const labelKey = documentClassLabelKey(docClass);
        // Nierozwiązywalna klasa (np. dokument miękko skasowany w trakcie obiegu → key
        // `documents:unknown`) → czytelny fallback zamiast surowego „unknown".
        return labelKey ? t(labelKey) : t('tenant.processes.approvals.groupBy.fallback.unnamed');
      }
      // Dokumenty z Symfonii Handel dzielą jeden kod celu dla trzech kartotek — rodzaj pełni tu
      // rolę klasy dokumentu, więc koszyki są trzy, nie jeden.
      if (group.key.startsWith('handel:')) {
        const kind = group.key.slice('handel:'.length);
        const labelKey = HANDEL_KIND_LABEL_KEYS[kind];
        return labelKey ? t(labelKey) : t('tenant.processes.approvals.groupBy.fallback.unnamed');
      }
      // Kartoteka własna: serwer podaje w etykiecie jej nazwę (etykieta = klucz, gdy kartoteki
      // już nie ma) — inaczej każda miałaby ten sam ogólny napis. Pusta albo złożona z samych
      // spacji etykieta to brak nazwy (nagłówek sekcji nie może być pusty) → ogólny napis.
      // Nazwa jest przycinana raz i w tej postaci zwracana.
      if (group.key.startsWith(CUSTOM_MODULE_CODE_PREFIX)) {
        const name = group.label?.trim() ?? '';
        if (name !== '' && name !== group.key) return name;
      }
      return t(moduleLabelI18nKey(group.key));
    default: {
      const exhaustive: never = groupBy;
      return String(exhaustive);
    }
  }
}
